import { graphql, useStaticQuery } from 'gatsby';

import { HrefLang } from './children/hreflang';
import OpenGraph from './children/open-graph';
import PageInfo from './children/page-info';
import SEO from './children/seo';
import StructuredData from './children/structured-data';
import Verification from './children/verification';
import PostSchema from './children/post-schema';

import { formatUrl } from './utils';
import type { IGlobalHead } from './types';

import { BreadcrumbsSchema } from '../../../components/json-ld/breadcrumbs';
import { useBreadcrumbs, useShouldDisplayBreadcrumbs } from '../../../hooks/useBreadcrumbs';

const GlobalHead = ({
  authors,
  children,
  excerpt,
  forceCanonicalTag = false,
  isFrontPage = false,
  isPaginated = false,
  isPost,
  mentions, 
  pathname,
  seo,
  shortTitle,
  translations = [],
  typeNameDataKey,
}: IGlobalHead) => {

  const data = useStaticQuery(graphql`
    query SiteLangCode {
      site {
        siteMetadata {
          langCode
        siteUrl
        pathPrefix
        }
      }
      wp {
        sites {
          path
          indexSite
        }
      }
    }
  `);

  const { siteMetadata: { langCode, siteUrl, pathPrefix } } = data.site;

  const { sites } = data.wp;
  
  const allowSiteIndex = () => {
    const indexEntireSite = sites.filter((site: { path: string | unknown[]; }) => (pathPrefix === '' && site.path === '/') || site.path.includes(pathPrefix));
    if (indexEntireSite.length > 0) {
      return indexEntireSite[0].indexSite;
    }
  };

  const canonical = formatUrl(seo?.canonical || pathname, siteUrl);
  const prevPath = seo?.prevPagePath ? formatUrl(seo?.prevPagePath, siteUrl) : undefined;
  const nextPath = seo?.nextPagePath ? formatUrl(seo?.nextPagePath, siteUrl) : undefined;

  const ogImage = seo?.opengraphImage?.publicUrl ? formatUrl(seo?.opengraphImage?.publicUrl, siteUrl) : undefined;
  const twitterImage = seo?.twitterImage?.publicUrl ? formatUrl(seo?.twitterImage?.publicUrl, siteUrl) : ogImage;

  const glossaryTerm = typeNameDataKey === 'wpGlossaryPage';
  const breadcrumbData = useBreadcrumbs(pathname, '', langCode);

  const metaRobotsNofollow = allowSiteIndex() ? seo?.metaRobotsNofollow : 'nofollow';
  const metaRobotsNoindex = allowSiteIndex() ? seo?.metaRobotsNoindex : 'noindex';
  const noIndexNoFollow = metaRobotsNoindex === 'noindex' || metaRobotsNofollow === 'nofollow';
  return (
    <>
      <PageInfo
        description={seo?.metaDesc}
        title={seo?.title}
      />
      <SEO
        canonical={canonical}
        metaRobotsNofollow={metaRobotsNofollow}
        metaRobotsNoindex={metaRobotsNoindex}
        uri={pathname}
        forceCanonicalTag={forceCanonicalTag}
        prevPagePath={prevPath}
        nextPagePath={nextPath}
        isPaginated={isPaginated}
      />
      {
        useShouldDisplayBreadcrumbs(pathname) &&
        <BreadcrumbsSchema
          {...breadcrumbData}
        />
      }
      {(isPost || glossaryTerm) && (
        <PostSchema
          authors={authors?.nodes}
          description={seo?.metaDesc}
          excerpt={excerpt}
          featuredImageUrl={seo?.opengraphImage?.publicUrl}
          glossaryTerm={glossaryTerm}
          mentions={mentions}
          dateModified={seo?.opengraphModifiedTime}
          publishDate={seo?.opengraphPublishedTime}
          shortTitle={shortTitle}
          title={seo?.title}
          url={pathname}
        />
      )}
      <HrefLang
        noIndexNoFollow={ noIndexNoFollow }
        translations={
          translations?.length > 0 ? translations : [
            {
              hreflang: langCode.toLowerCase(),
              url: canonical,
            },
          ]
        }
      />
      <OpenGraph
        description={seo?.opengraphDescription}
        image={ogImage}
        locale={langCode.replace('-', '_')}
        modifiedTime={seo?.opengraphModifiedTime}
        publisher={seo?.opengraphPublisher}
        siteName={seo?.opengraphSiteName}
        title={seo?.opengraphTitle}
        twitterDescription={seo?.twitterDescription}
        twitterImage={twitterImage}
        twitterTitle={seo?.twitterTitle || seo?.opengraphTitle}
        url={canonical}
      />
      {isFrontPage && (
        <StructuredData />
      )}
      <Verification />
      {children}
    </>
  );
};

export default GlobalHead;
